// User Management Constants

/**
 * Minimum password length enforced across all password creation and reset flows.
 * Single source of truth — used in LostPassword, UserModal, UserDetailsTab, and ChangePasswordModal.
 */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * Maps subcontractor numeric values to human-readable labels
 */
export const SUBCONTRACTOR_TYPE_MAP: Record<number, string> = {
  0: 'Normal',
  1: 'Subcontractor',
  2: 'Funding Source',
  3: 'Center User',
  4: 'Ecolane',
};

/**
 * Permission operations available for resources
 */
export enum PermissionOperation {
  View = 'View',
  Create = 'Create',
  Update = 'Update',
  Delete = 'Delete',
}

/**
 * Additional operations specific to certain resources
 */
export const RESOURCE_OPERATIONS = {
  ALERT_MESSAGE: {
    VIEW: 'View',
    UPDATE: 'Update',
    SEND_MESSAGE: 'Send message',
  },
  ARCHIVE_SCHEDULE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
  },
  ARCHIVE_TRIP: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    EXPORT_TRIPS: 'Export trips',
    POST_VERIFY_TRIPS: 'Post-verify trips',
    RECONCILE_TRIPS: 'Reconcile trips',
    REVIEW_TRIPS: 'Review trips',
    TRIP_COMPLETION_UPLOAD: 'Trip completion upload',
  },
  BATCH_JOB: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  BILLING_CODE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  BILLING_CODE_SET: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  CALENDAR: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  CENTER: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  CLIENT: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    EXPORT_CLIENTS: 'Export clients',
  },
  CLIENT_TRANSACTION: {
    VIEW: 'View',
    CREATE: 'Create',
  },
  CONFIGURATION_PARAMETER: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
  },
  CUSTOM_VALUE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  DASHBOARD: {
    VIEW: 'View',
  },
  DEVICE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  DRIVER: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
    LOG_OUT_DRIVERS: 'Log out drivers',
  },
  DRIVER_ADDITIONAL_INFORMATION: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  DRIVER_INCIDENT_RECORD: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  EXPENSE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
    EXPORT_EXPENSES: 'Export expenses',
  },
  EXPORT_FILE: {
    VIEW: 'View',
  },
  FEEDBACK_RECORD: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  FILTER_SET: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  FIXED_ROUTE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  FUNDING_SOURCE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  INSIGHTS: {
    VIEW: 'View',
  },
  INSPECTION_FORM: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  INSPECTION_LIST: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
    LINK_ARCHIVE_SCHEDULE: 'Link archive schedule',
  },
  LIQUIDATED_DAMAGE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  LOCATION: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  MDT_DEVICE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  MESSAGE_TEMPLATE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  NOTIFICATIONS: {
    VIEW: 'View',
  },
  POLYGON: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  PROVIDER: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
    MANAGE_TRIP_LIMITS: 'Manage trip limits',
  },
  PROVIDER_DENY_LIST: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  PROVIDER_RATE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  REIMBURSEMENT: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
    APPROVE_OR_DENY: 'Approve or deny reimbursements',
    DISBURSE: 'Disburse reimbursements',
    EXPORT: 'Export reimbursements',
    ROLLBACK_TO_REVIEW: 'Rollback reimbursement to In Review',
    SPLIT_APPROVED: 'Split reimbursement for approved expenses',
    VERIFY_EXPENSES: 'Verify reimbursement expenses',
  },
  REPORT_TEMPLATES: {
    VIEW: 'View',
    UPDATE: 'Update',
    AD_HOC_REPORTING: 'Ad hoc reporting',
    MANAGE_REPORTING: 'Manage reporting',
  },
  REQUIRED_FIELD: {
    VIEW: 'View',
    UPDATE: 'Update',
  },
  RIDER_POLICY_VIOLATION: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  ROADCALL_RECORD: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  RUN: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    ASSIGN_VEHICLES: 'Assign vehicles',
    CREATE_AVAILABILITY: 'Create availability',
    DELETE_AVAILABILITY: 'Delete availability',
    EDIT_AVAILABILITY_FUTURE: 'Edit availability for future',
    EDIT_AVAILABILITY_TODAY: 'Edit availability for today',
    SYNC_AVAILABILITIES: 'Sync availabilities',
  },
  RUN_POOL: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  SCHEDULE: {
    VIEW: 'View',
    UPDATE: 'Update',
    COMPLETE_SCHEDULES: 'Complete schedules',
    EDIT_STOP_ORDER: 'Edit schedule stop order',
    OPTIMIZE_SCHEDULES: 'Optimize schedules',
  },
  SCHEDULE_CUSTOM_FIELD: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  SCHEDULE_EVENT: {
    VIEW: 'View',
  },
  SELF_SERVICE_ACCOUNT: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  SERVICE_AREA: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  SERVICE_RECORD: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  SERVICE_RECORD_COMMENT: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  STREET_SEGMENT: {
    VIEW: 'View',
    UPDATE: 'Update',
  },
  SUBSCRIPTION: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    CANCEL_SUBSCRIPTIONS: 'Cancel subscriptions',
  },
  SURCHARGE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  TRIP: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    ACCEPT_TRIPS: 'Accept trips',
    CANCEL_TRIPS: 'Cancel trips',
    CHANGE_PROVIDER: 'Change trip provider',
    CHANGE_TIME_WINDOWS: 'Change trip time windows',
    CHANGE_TRIP_GROUP: 'Change trip trip group',
    CONFIRM_WILL_CALL: 'Confirm will-call trips',
    EXPORT_COMPLETION: 'Export trip completion',
    LOCK_SCHEDULED_PROVIDER: 'Lock scheduled provider',
    LOCK_FOR_DISPATCH: 'Lock trips for dispatch',
    PRE_VERIFY: 'Pre-verify trips',
    REASSIGN: 'Reassign trips',
    RESCHEDULE: 'Reschedule trips',
    SCHEDULE_WITH_VIOLATIONS: 'Schedule trips with violations',
    SET_TARGET_RUNS: 'Set/Change trip target runs',
    SHIFT_TIMES: 'Shift trip times',
  },
  TRIP_EVENT: {
    VIEW: 'View',
  },
  TRIP_EXPORT_PROFILE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  TRIP_GROUP: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
    OPTIMIZE: 'Optimize trip groups',
  },
  TRIP_PURPOSE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  USER: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  USER_EVENT: {
    VIEW: 'View',
  },
  USER_ROLE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
  VEHICLE: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
  },
  VEHICLE_EVENT: {
    VIEW: 'View',
  },
  VEHICLE_MAINTENANCE_RECORD: {
    VIEW: 'View',
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  },
} as const;

/**
 * Module permissions
 */
export const MODULE_PERMISSIONS = {
  BULK_IMPORT: 'Bulk import',
  EDI: 'EDI',
  REPORTING: 'Reporting',
} as const;

/**
 * Resource names for permissions
 */
export const PERMISSION_RESOURCES = {
  ALERT_MESSAGE: 'Alert/Message',
  ARCHIVE_SCHEDULE: 'Archive schedule',
  ARCHIVE_TRIP: 'Archive trip',
  BATCH_JOB: 'Batch job',
  BILLING_CODE: 'Billing code',
  BILLING_CODE_SET: 'Billing code set',
  CALENDAR: 'Calendar',
  CENTER: 'Center',
  CLIENT: 'Client',
  CLIENT_TRANSACTION: 'Client transaction',
  CONFIGURATION_PARAMETER: 'Configuration parameter',
  CUSTOM_VALUE: 'Custom value',
  DASHBOARD: 'Dashboard',
  DEVICE: 'Mobility device',
  DRIVER: 'Driver',
  DRIVER_ADDITIONAL_INFORMATION: 'Driver additional information',
  DRIVER_INCIDENT_RECORD: 'Driver incident record',
  EXPENSE: 'Expense',
  EXPORT_FILE: 'Export file',
  FEEDBACK_RECORD: 'Feedback record',
  FILTER_SET: 'Filter set',
  FIXED_ROUTE: 'Fixed route',
  FUNDING_SOURCE: 'Funding source',
  INSIGHTS: 'Insights',
  INSPECTION_FORM: 'Inspection form',
  INSPECTION_LIST: 'Inspection list',
  LIQUIDATED_DAMAGE: 'Liquidated damage',
  LOCATION: 'Location',
  MDT_DEVICE: 'MDT device',
  MESSAGE_TEMPLATE: 'Message template',
  NOTIFICATIONS: 'Notifications',
  POLYGON: 'Polygon',
  PROVIDER: 'Provider',
  PROVIDER_DENY_LIST: 'Provider deny list',
  PROVIDER_RATE: 'Provider rate',
  REIMBURSEMENT: 'Reimbursement',
  REPORT_TEMPLATES: 'Report templates',
  REQUIRED_FIELD: 'Required field',
  RIDER_POLICY_VIOLATION: 'Rider policy violation',
  ROADCALL_RECORD: 'Roadcall record',
  RUN: 'Run',
  RUN_POOL: 'Run pool',
  SCHEDULE: 'Schedule',
  SCHEDULE_CUSTOM_FIELD: 'Schedule custom field',
  SCHEDULE_EVENT: 'Schedule event',
  SELF_SERVICE_ACCOUNT: 'Self-service account',
  SERVICE_AREA: 'Service area',
  SERVICE_RECORD: 'Service record',
  SERVICE_RECORD_COMMENT: 'Service record comment',
  STREET_SEGMENT: 'Street segment',
  SUBSCRIPTION: 'Subscription',
  SURCHARGE: 'Surcharge',
  TRIP: 'Trip',
  TRIP_EVENT: 'Trip event',
  TRIP_EXPORT_PROFILE: 'Trip export profile',
  TRIP_GROUP: 'Trip group',
  TRIP_PURPOSE: 'Trip purpose',
  USER: 'User',
  USER_EVENT: 'User event',
  USER_ROLE: 'User role',
  VEHICLE: 'Vehicle',
  VEHICLE_EVENT: 'Vehicle event',
  VEHICLE_MAINTENANCE_RECORD: 'Vehicle maintenance record',
} as const;

/**
 * Type for permission resource keys
 */
export type PermissionResourceKey = keyof typeof PERMISSION_RESOURCES;

/**
 * Type for resource operation keys
 */
export type ResourceOperationKey = keyof typeof RESOURCE_OPERATIONS;

/**
 * Center status mappings
 */
export const CENTER_STATUS = {
  ENABLED: 'Enabled',
  DISABLED: 'Disabled',
} as const;

/**
 * Center location filter mappings
 */
export const CENTER_LOCATION_FILTER = {
  NO_VALUE: 'Any location',
  YES_VALUE: 'Only specified locations',
} as const;

/**
 * Center cancel permission mappings
 */
export const CENTER_CANCEL_PERMISSION = {
  0: 'Any location',
  1: 'Only specified locations',
} as const;